import { MAILSTATE, PMSTATE, SETTINGSSTATE, USERSSTATE } from '.'
import type {
    ChangesAlerts,
    ChangesMessages,
    RoomHelperFrontendEvent,
} from '../../types'
import type { GenericFcsPacket } from '../../types/mfc'
import { MyWebcamServerData } from '../../types/state'
import { AppStateType, SettingsType } from '../constants/enums'
import {
    calculateNewsSettings,
    showNewsInPrivateMessages,
    showsNewsInRooms,
} from '../mfcLogic/api/news'
import {
    FCOPT_BASICMUTE,
    FCOPT_GUESTMUTE,
    FCTYPE_CHANOP,
    FCTYPE_QUIETCHAN,
    FCTYPE_ZBAN,
} from '../mfcLogic/mfcConstants'
import { initMyWebcam } from '../mfcLogic/videoPublisher'
import {
    setPMChatHistoryMessagesState,
    setPMChatNewsState,
    setPMChatOnlyIfMissingState,
    setPMChatTextState,
    setPMChatUniqueState,
    setPMUnreadStateFragment,
} from '../state/state.pm'
import { safeDecodeUriComponent } from '../utilities/safeDecodeUriComponent'
import { getRoomsState, getStateUser } from './getState'
import { isQuickReplyOpen } from './internal/floaters'
import {
    setPMBatchLoadingState,
    setPMChatExaustedState,
    setPMChatLoadingState,
    setPMChatUnreadState,
    setPMExaustedState,
    setPMLoadingState,
} from './internal/pms'
import { setRoomState } from './internal/rooms'
import { sessionToID, slugToID } from './internal/users'
import {
    openAnnounceDialog,
    openRuleViolationDialog,
} from './modals/state.dialog'
import { insertAlerts } from './state.alerts'
import { updateAppState, updateMyWebcamState } from './state.app'
import { setArchiveMessagesState } from './state.archives'
import { emitMailboxUpdate } from './state.mail'
import {
    clearRoomMessagesBySid,
    setRoomMessagesState,
    setRoomNewsMessage,
    setRoomTipState,
} from './state.rooms'

export function onINBOX(payload: GenericFcsPacket) {
    const unreadCount = payload.arg1
    MAILSTATE.mail.unread = unreadCount
    emitMailboxUpdate(MAILSTATE)
    return
}

export function onPUBLISH(payload: { payload: MyWebcamServerData }) {
    const { payload: serverData } = payload
    updateAppState(AppStateType.MYWEBCAM, { serverData }, true)
    if (!serverData) return
    const { ctx, tkx, vidserv } = serverData
    const { id: userId, session: sessionId } = USERSSTATE.users[slugToID['me']]
    if (userId && sessionId && ctx && tkx && vidserv) {
        const eventDispatcher = updateMyWebcamState
        initMyWebcam({ sessionId, userId, ctx, tkx, vidserv, eventDispatcher })
    }
}

export const onALERTS = (data: { alerts: ChangesAlerts[] }) => {
    const { alerts } = data
    insertAlerts(alerts)
}

const getMessageAndDistinguish = (event) => {
    try {
        const message =
            typeof event.payload === 'string'
                ? (safeDecodeUriComponent(event.payload as string) as any)
                : event.payload?.msg ||
                  event.payload?.text ||
                  event.payload?.message ||
                  JSON.stringify(event.payload || '', null, 4)

        const distinguish = event.payload?.msg
            ? '`'
            : event.payload?.text
              ? '``'
              : event.payload?.message
                ? '```'
                : ''

        return message + distinguish
    } catch (error) {
        console.error(error)
    }

    return event.payload
}

export const onRULEVIOLATION = (event: GenericFcsPacket & { payload: any }) => {
    openRuleViolationDialog({ message: getMessageAndDistinguish(event) })
}

export const onANNOUNCE = (event: GenericFcsPacket & { payload: any }) => {
    openAnnounceDialog({ message: getMessageAndDistinguish(event) })
}

export const onMessages = (data: { messages: ChangesMessages[] }) => {
    const { messages } = data

    messages.forEach((item) => {
        const {
            sessionLabel,
            isExhausted,
            isHistory,
            isArchive,
            clearChat,
            fragment,
            messages,
            chatType,
            message,
            unread,
            news,
            show,
            done,
            date,
            pm,
            id,
        } = item

        if (clearChat) {
            const { sids = [], uids = [], min } = clearChat
            clearRoomMessagesBySid(id, sids, uids, min)
            return
        }

        if (isArchive) {
            if (done) {
                return
            }
            const config = { sessionLabel, messages, chatType, date }
            setArchiveMessagesState(id, config)
            return
        }

        if (pm && isHistory) {
            setPMChatHistoryMessagesState(id, messages)
            return
        }

        if (message === null) {
            console.log(
                '[onMessages] NULL MESSAGE',
                JSON.stringify(message, null, 4),
            )
        }

        if (news) {
            const ueopt = SETTINGSSTATE[SettingsType.NEWS_NOTIFICATIONS]?.news
            const newsSettings = calculateNewsSettings(ueopt)
            const showNewsInPM = showNewsInPrivateMessages(
                newsSettings.FCNEWSOPT_IN_PM,
            )
            const showNewsInRoom = showsNewsInRooms(
                newsSettings.FCNEWSOPT_IN_CHAN,
            )

            if (showNewsInPM) {
                setPMChatNewsState(message)
            }

            if (showNewsInRoom) {
                setRoomNewsMessage(message)
            }

            return
        }

        if (typeof show === 'string') {
            if (show !== 'group') {
                setPMChatOnlyIfMissingState(id, message, show + 'Show')
            }

            if (show !== 'club') {
                setRoomMessagesState(message, id)
            }

            return
        }

        if (!pm) {
            if (message?.tip) {
                const {
                    tipper,
                    timestamp,
                    shareTip,
                    tipColor,
                    amountInt,
                    images,
                    title,
                    model,
                    text,
                    note,
                    url,
                } = message

                setRoomTipState(
                    {
                        amountInt,
                        system: true,
                        tip: true,
                        displayed: false,
                        shareTip,
                        tipColor,
                        tipper,
                        timestamp,
                        images,
                        title,
                        model,
                        text,
                        note,
                        url,
                    },
                    id,
                )
            }

            setRoomMessagesState(message, id)
            return
        }

        if (message !== undefined) {
            if (message.basic) {
                setPMChatUniqueState(id, message, 'basic')
            } else {
                setPMChatTextState(id, message)
            }

            if (message.preview) {
                setPMLoadingState(false)
            }
        }

        if (unread !== undefined) {
            if (fragment) {
                setPMUnreadStateFragment(id, unread)
                return
            }
            setPMChatUnreadState(id, unread)
        }

        if (isExhausted !== undefined) {
            if (id) {
                setPMChatExaustedState(id, isExhausted)
                setPMChatLoadingState(id, false)
            } else {
                setPMExaustedState(isExhausted)
                setPMBatchLoadingState(false)
                setPMLoadingState(false)
            }
        }

        if (pm && done) {
            if (id) {
                setPMChatLoadingState(id, false)
            } else {
                setPMBatchLoadingState(false)
                setPMLoadingState(false)
            }
        }

        if (!message) {
            return
        }

        const { history, loading, preview, system } = message

        if (history || loading || system || preview) {
            return
        }

        const roomSettings = SETTINGSSTATE[SettingsType.ROOM]
        const inlinePM = roomSettings['inlinePM']
        const availableSpaceView = roomSettings['availableSpaceView']

        if (inlinePM === 3) {
            // send pm to all rooms
            Object.keys(getRoomsState()).forEach((id) => {
                setRoomMessagesState(
                    { ...message, pmCommand: true },
                    Number(id),
                )
            })
        }

        if (inlinePM === 1) {
            // send pm only to the room of this model
            setRoomMessagesState({ ...message, pmCommand: true }, id)
        }

        if (inlinePM === 32) {
            // send pm only to the room of this model only if the PM command was
            // used (check the PMs for the model)
            const { wasPMCommandUsed } = PMSTATE.chats[id] || {}

            if (
                wasPMCommandUsed &&
                !isQuickReplyOpen(id, true) &&
                availableSpaceView !== 'pm'
            ) {
                setRoomMessagesState({ ...message, pmCommand: true }, id)
            }
        }
    })
}

export const onROOMHELPER = (event: RoomHelperFrontendEvent) => {
    const { op, modelId, isMe, payload, message } = event

    switch (op) {
        case 'authorized': {
            if (isMe) {
                const model = getStateUser(modelId)
                const modelName = model?.name ?? String(modelId)
                setRoomMessagesState(
                    {
                        text: `You are now ${modelName}'s room helper.`,
                        system: true,
                        timestamp: Date.now(),
                        roomHelper: true,
                    },
                    modelId,
                )
            }
            break
        }

        case 'removed': {
            if (isMe) {
                const model = getStateUser(modelId)
                const modelName = model?.name ?? String(modelId)
                setRoomMessagesState(
                    {
                        text: `You are no longer ${modelName}'s room helper.`,
                        system: true,
                        timestamp: Date.now(),
                        roomHelper: true,
                    },
                    modelId,
                )
            }
            break
        }

        case 'command_result': {
            processCommandResult(modelId, payload)
            break
        }

        case 'error': {
            setRoomMessagesState(
                {
                    text: `Room Helper Error: ${message}`,
                    system: true,
                    error: true,
                    timestamp: Date.now(),
                    roomHelper: true,
                },
                modelId,
            )
            break
        }
    }
}

function processCommandResult(modelId: number, command: any) {
    if (!command || typeof command !== 'object') return

    if (command._msg) {
        showRoomHelperMessage(modelId, command._msg)
        return
    }

    if (command.type === FCTYPE_ZBAN) {
        if (command.op === 'ban' && command.ban) {
            showRoomHelperMessage(
                modelId,
                `${command.ban.username} has been banned from the chat room.`,
            )
            trackBannedUser(
                modelId,
                command.ban.username,
                command.ban.sid,
                true,
            )
        } else if (command.op === 'unban') {
            showRoomHelperMessage(
                modelId,
                `${command.username} has been unbanned from the chat room.`,
            )
            trackBannedUser(modelId, command.username, command.sid, false)
        } else if (command.op === 'mute') {
            showRoomHelperMessage(
                modelId,
                `${command.mute.username} has been muted.`,
            )
            trackMutedUser(
                modelId,
                command.mute.username,
                command.mute.sid,
                true,
            )
        } else if (command.op === 'unmute') {
            showRoomHelperMessage(
                modelId,
                `${command.username} has been unmuted.`,
            )
            trackMutedUser(modelId, command.username, command.sid, false)
        }
    } else if (command.type === FCTYPE_CHANOP && command.op === 'kick') {
        if (command._err && command._msg) {
            showRoomHelperMessage(modelId, `Error: ${command._msg}`)
        } else if (command.kicked?.length) {
            const names = command.kicked.map((k) => k.nm).join(', ')
            const verb = command.kicked.length === 1 ? 'has' : 'have'
            showRoomHelperMessage(
                modelId,
                `${names} ${verb} been kicked from the chat room.`,
            )
        } else {
            showRoomHelperMessage(
                modelId,
                'No users have been kicked. Check that the username(s) are correct and the user(s) are in the room.',
            )
        }
    } else if (command.type === FCTYPE_QUIETCHAN) {
        const isGuestMute = command.optmask === FCOPT_GUESTMUTE
        const userType = isGuestMute ? 'Guest' : 'Basic'
        const flag = isGuestMute ? FCOPT_GUESTMUTE : FCOPT_BASICMUTE
        const muted = (command.optvalues & flag) !== 0
        showRoomHelperMessage(
            modelId,
            `${userType} mute is now ${muted ? 'ON' : 'OFF'}.`,
        )
    }
}

function trackRoomHelperUserList(
    modelId: number,
    key: 'mutedUsers' | 'bannedUsers',
    username?: string,
    sid?: number,
    add?: boolean,
) {
    const userId =
        (username && slugToID[username.toLowerCase()]) ||
        (sid && sessionToID[sid])
    if (!userId) return

    const { [modelId]: room } = getRoomsState()
    const list = room?.[key] ?? []

    if (add) {
        if (!list.includes(userId)) {
            setRoomState({ id: modelId, room: { [key]: [...list, userId] } })
        }
    } else {
        setRoomState({
            id: modelId,
            room: { [key]: list.filter((id) => id !== userId) },
        })
    }
}

function trackMutedUser(
    modelId: number,
    username?: string,
    sid?: number,
    muted?: boolean,
) {
    trackRoomHelperUserList(modelId, 'mutedUsers', username, sid, muted)
}

function trackBannedUser(
    modelId: number,
    username?: string,
    sid?: number,
    banned?: boolean,
) {
    trackRoomHelperUserList(modelId, 'bannedUsers', username, sid, banned)
}

function showRoomHelperMessage(modelId: number, text: string) {
    setRoomMessagesState(
        {
            text,
            system: true,
            timestamp: Date.now(),
            roomHelper: true,
        },
        modelId,
    )
}

export const onROOMDATA = (event: any) => {
    const { op, modelId, countdown, previous, message } = event

    if (op === 'error') {
        setRoomMessagesState(
            {
                text: `Countdown Error: ${message}`,
                system: true,
                error: true,
                timestamp: Date.now(),
                roomHelper: true,
            },
            modelId,
        )
        return
    }

    if (op === 'update' && countdown) {
        // update room state with countdown data
        setRoomState(
            {
                id: modelId,
                room: {
                    countdown: {
                        total: countdown.total,
                        sofar: countdown.sofar,
                        active: countdown.countdown,
                    },
                },
            },
            { immediate: true },
        )

        // show verbose countdown message in chat (only for room helpers)
        const model = getStateUser(modelId)
        if (model?.isRoomHelper) {
            const msg = resolveCountdownMessage(previous, countdown)
            if (msg) {
                setRoomMessagesState(
                    {
                        text: msg,
                        system: true,
                        timestamp: Date.now(),
                        countdown: true,
                    },
                    modelId,
                )
            }
        }
    }
}

type CountdownData = {
    model: number
    total: number
    sofar: number
    countdown: boolean
    src?: 'add' | 'update' | 'notify'
    topic?: string
}

function resolveCountdownMessage(
    previous: CountdownData | null,
    current: CountdownData,
): string | null {
    const { total, sofar, src } = current

    if (typeof total !== 'number' || typeof sofar !== 'number') {
        return null
    }

    const remaining = total - sofar
    const prevActive = previous?.countdown ?? false
    const currActive = current.countdown
    const nChange = prevActive === currActive ? 0 : currActive ? 1 : -1

    if (nChange !== 0 || src !== 'notify') {
        if (nChange === 1) {
            if (sofar === 0) {
                return `* Tip countdown started for ${total} tokens!`
            }
            return `* Tip countdown started for ${total} tokens! (${sofar} of ${total} total)`
        }

        if (nChange === -1) {
            if (sofar < total) {
                return `* Tip countdown ended with ${remaining} tokens still remaining.`
            }
            return '* Tip countdown completed, no remaining tokens!'
        }

        // nChange === 0, src !== 'notify'
        if (sofar === 0) {
            return `* Updated tip countdown to ${total} tokens!`
        }
        return `* Updated tip countdown to ${remaining} tokens! (${sofar} of ${total} total)`
    }

    // nChange === 0 && src === 'notify'
    return `* ${remaining} tokens remain in the countdown!`
}
